import { useAuthStore, authEvents } from './chunk-XW7GX6IX.js';
import { isAuthDisabled, getDevSession, getApiMode, resolveContextBaseUrl } from '@quickbee/api-config';

// src/internal/jwt.ts
function base64UrlDecode(segment) {
  const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
  if (typeof atob === "function") {
    return atob(base64);
  }
  return Buffer.from(base64, "base64").toString("utf-8");
}
function normaliseClaims(raw) {
  const rolesClaim = raw["roles"] ?? raw["cognito:groups"];
  return {
    ...raw,
    userId: raw["userId"] ?? raw["sub"],
    tenantId: raw["tenantId"] ?? raw["tenant_id"],
    // Distinct from `userId`/`sub` — see `AuthUser.customerId`. Decoded but never
    // surfaced until now, which left customer-keyed screens passing the auth subject
    // where an account id was required.
    customerId: raw["customerId"] ?? raw["customer_id"],
    roles: Array.isArray(rolesClaim) ? rolesClaim : [],
    kycTier: raw["kycTier"] ?? raw["kyc_tier"] ?? "KYC0",
    // Cognito mints no session id; the backend's single-active-session check tolerates its
    // absence, and `X-Device-Session-Id` is then simply not sent.
    sessionId: raw["sessionId"] ?? raw["origin_jti"] ?? "",
    exp: raw["exp"]
  };
}
function decodeAuthToken(token) {
  const parts = token.split(".");
  const payload = parts[1];
  if (parts.length !== 3 || !payload) return null;
  try {
    const json = base64UrlDecode(payload);
    return normaliseClaims(JSON.parse(json));
  } catch {
    return null;
  }
}
function isTokenExpired(claims, skewMs = 0) {
  if (!claims.exp) return true;
  return Date.now() >= claims.exp * 1e3 - skewMs;
}

// src/internal/mockAuth.ts
var MOCK_OTP = "555555";
var MOCK_TENANT_ID = "11111111-1111-1111-1111-111111111111";
var MOCK_SESSION_STORAGE_KEY = "qb_mock_session";
var MOCK_TOKEN_TTL_SECONDS = 3600;
function readStoredPhone() {
  try {
    if (typeof sessionStorage === "undefined") return null;
    return sessionStorage.getItem(MOCK_SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}
function storePhone(phone) {
  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(MOCK_SESSION_STORAGE_KEY, phone);
    }
  } catch {
  }
}
function clearMockSession() {
  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem(MOCK_SESSION_STORAGE_KEY);
    }
  } catch {
  }
}
function base64UrlEncode(input) {
  const bytes = new TextEncoder().encode(input);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  const base64 = typeof btoa === "function" ? btoa(binary) : Buffer.from(input, "utf-8").toString("base64");
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function hash32(value) {
  let h = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}
function seededUuid(seed) {
  const a = hash32(seed).toString(16).padStart(8, "0");
  const b = hash32(`${seed}:1`).toString(16).padStart(8, "0");
  const c = hash32(`${seed}:2`).toString(16).padStart(8, "0");
  const d = hash32(`${seed}:3`).toString(16).padStart(8, "0");
  return `${a}-${b.slice(0, 4)}-4${b.slice(5, 8)}-a${c.slice(1, 4)}-${c.slice(4, 8)}${d}`;
}
function maskPhone(phone) {
  const digits = phone.replace(/\D/g, "");
  if (digits.length < 4) return phone;
  return `+91 \u2022\u2022\u2022\u2022\u2022 ${digits.slice(-4)}`;
}
function mintAccessToken(phone, tenantId) {
  const nowSeconds = Math.floor(Date.now() / 1e3);
  const header = { alg: "none", typ: "JWT" };
  const payload = {
    sub: seededUuid(phone),
    customer_id: seededUuid(`${phone}:customer`),
    tenant_id: tenantId,
    "cognito:groups": ["SAVER"],
    // Fully-verified, so KYC-gated demo screens are reachable rather than
    // showing an upgrade prompt no mock backend can satisfy.
    kyc_tier: "KYC3",
    origin_jti: seededUuid(`${phone}:session`),
    phone_number: phone,
    iat: nowSeconds,
    exp: nowSeconds + MOCK_TOKEN_TTL_SECONDS
  };
  return `${base64UrlEncode(JSON.stringify(header))}.${base64UrlEncode(
    JSON.stringify(payload)
  )}.mock-signature-not-verifiable`;
}
function mockProfile(phone) {
  return {
    id: seededUuid(`${phone}:customer`),
    userType: "SAVER",
    status: "ACTIVE",
    fullName: "QuickBee Saver",
    phoneMasked: maskPhone(phone)
  };
}
function mintMockSession(phone, options) {
  const tenantId = options.tenantId ?? MOCK_TENANT_ID;
  storePhone(phone);
  return {
    accessToken: mintAccessToken(phone, tenantId),
    expiresAt: Date.now() + MOCK_TOKEN_TTL_SECONDS * 1e3,
    profile: mockProfile(phone)
  };
}
function restoreMockSession(options) {
  const phone = readStoredPhone();
  if (!phone) return null;
  return mintMockSession(phone, options);
}

// src/internal/sessionClient.ts
var DEVICE_ID_STORAGE_KEY = "qb_device_id";
function getBrowserDeviceInfo() {
  let deviceId = null;
  try {
    if (typeof localStorage !== "undefined") {
      deviceId = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
      if (!deviceId) {
        deviceId = typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `dev-${Math.abs(Date.now()).toString(36)}`;
        localStorage.setItem(DEVICE_ID_STORAGE_KEY, deviceId);
      }
    }
  } catch {
  }
  return {
    deviceId: deviceId ?? `dev-${Math.abs(Date.now()).toString(36)}`,
    platform: "web",
    ...typeof navigator !== "undefined" && navigator.userAgent ? { userAgent: navigator.userAgent } : {}
  };
}
function newAuthRequestId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `req-${Math.abs(Date.now()).toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;
}
function usingMockTransport() {
  return getApiMode() === "mock";
}
function authUrl(options, path) {
  const base = (options.baseUrl ?? resolveContextBaseUrl("identity")).replace(/\/+$/, "");
  return `${base}/auth${path}`;
}
function authHeaders(options, { hasBody, idempotencyKey }) {
  const headers = {};
  if (hasBody) headers["Content-Type"] = "application/json";
  if (options.tenantId) headers["X-Tenant-ID"] = options.tenantId;
  if (idempotencyKey) headers["X-Idempotency-Key"] = idempotencyKey;
  return headers;
}
function toSessionTokens(payload) {
  if (!payload || typeof payload.accessToken !== "string" || !payload.accessToken) {
    return null;
  }
  const ttlMs = Number(payload.expiresInSeconds) * 1e3;
  return {
    accessToken: payload.accessToken,
    expiresAt: Date.now() + (Number.isFinite(ttlMs) ? ttlMs : 0),
    ...payload.profile ? { profile: payload.profile } : {}
  };
}
async function readFailure(res, fallback) {
  let code = "UNKNOWN_ERROR";
  let message = fallback;
  try {
    const envelope = await res.json();
    const first = envelope?.errors?.[0];
    if (first?.code) code = first.code;
    if (first?.message) message = first.message;
  } catch {
  }
  return { status: res.status, code, message };
}
async function readTokens(res) {
  if (!res.ok) return null;
  try {
    const envelope = await res.json();
    return toSessionTokens(envelope?.data);
  } catch {
    return null;
  }
}
async function readAccepted() {
  return true;
}
async function postJson(options, path, body, idempotencyKey, fallbackMessage, readOk) {
  const fetchImpl = options.fetchImpl ?? fetch;
  let res;
  try {
    res = await fetchImpl(authUrl(options, path), {
      method: "POST",
      credentials: "include",
      headers: authHeaders(options, { hasBody: true, idempotencyKey }),
      body: JSON.stringify(body)
    });
  } catch {
    return {
      ok: false,
      error: {
        status: 0,
        code: "NETWORK_ERROR",
        message: "Could not reach the server. Check your connection and try again."
      }
    };
  }
  if (!res.ok) {
    return { ok: false, error: await readFailure(res, fallbackMessage) };
  }
  const data = await readOk(res);
  if (data === null) {
    return {
      ok: false,
      error: { status: res.status, code: "MALFORMED_RESPONSE", message: fallbackMessage }
    };
  }
  return { ok: true, data };
}
async function refreshSession(options) {
  if (usingMockTransport()) return restoreMockSession(options);
  const fetchImpl = options.fetchImpl ?? fetch;
  try {
    const res = await fetchImpl(authUrl(options, "/refresh"), {
      method: "POST",
      credentials: "include",
      headers: authHeaders(options, {})
    });
    return await readTokens(res);
  } catch {
    return null;
  }
}
async function verifyOtp(options, request) {
  if (usingMockTransport()) {
    if (request.code !== MOCK_OTP) {
      return {
        ok: false,
        error: {
          status: 400,
          code: "INVALID_OTP",
          message: `Incorrect code. This is a demo build \u2014 use ${MOCK_OTP}.`
        }
      };
    }
    return { ok: true, data: mintMockSession(request.phone, options) };
  }
  return postJson(
    options,
    "/verify-otp",
    // Backend `VerifyOtpRequest` fields: phone, otp, deviceId, platform, userAgent.
    {
      phone: request.phone,
      otp: request.code,
      deviceId: request.device.deviceId,
      platform: request.device.platform,
      ...request.device.userAgent ? { userAgent: request.device.userAgent } : {}
    },
    `auth-verify-otp:${request.requestId}`,
    "Invalid or expired OTP. Please try again.",
    readTokens
  );
}
async function passwordLogin(options, request) {
  if (usingMockTransport()) {
    return {
      ok: false,
      error: {
        status: 501,
        code: "MOCK_MODE_PASSWORD_LOGIN_UNAVAILABLE",
        message: `Password sign-in needs the live backend. This is a demo build \u2014 sign in with any mobile number and OTP ${MOCK_OTP}.`
      }
    };
  }
  return postJson(
    options,
    "/password-login",
    // Backend `PasswordLoginRequest` fields: username, password, deviceId, platform, userAgent.
    {
      username: request.username,
      password: request.password,
      deviceId: request.device.deviceId,
      platform: request.device.platform,
      ...request.device.userAgent ? { userAgent: request.device.userAgent } : {}
    },
    `auth-password-login:${request.requestId}`,
    "Invalid mobile number/email or password.",
    readTokens
  );
}
async function requestOtp(options, request) {
  if (usingMockTransport()) return { ok: true, data: true };
  return postJson(
    options,
    "/login",
    { phone: request.phone },
    `auth-login:${request.requestId}`,
    "Could not send the OTP. Please try again.",
    readAccepted
  );
}
async function resendOtp(options, request) {
  if (usingMockTransport()) return { ok: true, data: true };
  return postJson(
    options,
    "/otp/resend",
    { phone: request.phone },
    `auth-otp-resend:${request.requestId}`,
    "Could not resend the OTP. Please try again.",
    readAccepted
  );
}
async function registerSaver(options, request) {
  if (usingMockTransport()) return { ok: true, data: true };
  return postJson(
    options,
    "/register",
    // Backend `RegisterSaverRequest` fields: phone (E.164), fullName, email.
    // `email` is `@Email`-validated but not `@NotBlank`, so an absent address is
    // legal — an empty string is not.
    {
      phone: request.phone,
      fullName: request.fullName,
      ...request.email?.trim() ? { email: request.email.trim() } : {}
    },
    `auth-register:${request.requestId}`,
    "Could not start registration. Please try again.",
    readAccepted
  );
}
async function logoutSession(options) {
  if (usingMockTransport()) {
    clearMockSession();
    return;
  }
  const fetchImpl = options.fetchImpl ?? fetch;
  try {
    await fetchImpl(authUrl(options, "/logout"), {
      method: "POST",
      credentials: "include",
      headers: {
        ...authHeaders(options, { idempotencyKey: `auth-logout:${newAuthRequestId()}` }),
        // The only authenticated call on this client — see `SessionClientOptions.accessToken`.
        ...options.accessToken ? { Authorization: `Bearer ${options.accessToken}` } : {}
      }
    });
  } catch {
  }
}

// src/internal/refreshCoordinator.ts
function createRefreshCoordinator() {
  let inFlight = null;
  return {
    attemptRefresh(options = {}) {
      if (!inFlight) {
        inFlight = performRefresh(options).finally(() => {
          inFlight = null;
        });
      }
      return inFlight;
    }
  };
}
async function performRefresh(options) {
  const store = options.authStore ?? useAuthStore;
  const result = await refreshSession({
    ...options.baseUrl !== void 0 ? { baseUrl: options.baseUrl } : {},
    ...options.fetchImpl ? { fetchImpl: options.fetchImpl } : {}
  });
  if (!result) {
    store.getState().clearAuth();
    authEvents.emit("sessionExpired", { reason: "refresh-failed" });
    options.onAuthFailure?.();
    return null;
  }
  store.getState().setAccessToken(result.accessToken);
  authEvents.emit("tokenRefreshed", { expiresAt: result.expiresAt });
  return result;
}
var sharedRefreshCoordinator = createRefreshCoordinator();

// src/internal/tokenRefresh.ts
var TOKEN_REFRESH_LEAD_MS = 6e4;
function scheduleTokenRefresh(expiresAt, clientOptions = {}) {
  let timerId;
  let stopped = false;
  const run = (nextExpiresAt) => {
    const delay = Math.max(0, nextExpiresAt - Date.now() - TOKEN_REFRESH_LEAD_MS);
    timerId = setTimeout(async () => {
      if (stopped) return;
      const result = await sharedRefreshCoordinator.attemptRefresh({
        ...clientOptions.baseUrl !== void 0 ? { baseUrl: clientOptions.baseUrl } : {},
        ...clientOptions.fetchImpl ? { fetchImpl: clientOptions.fetchImpl } : {}
      });
      if (stopped || !result) return;
      run(result.expiresAt);
    }, delay);
  };
  run(expiresAt);
  return {
    stop: () => {
      stopped = true;
      clearTimeout(timerId);
    }
  };
}
function buildAuthUser(tokens) {
  const claims = decodeAuthToken(tokens.accessToken);
  if (!claims || !claims.userId || !claims.tenantId) return null;
  const user = {
    userId: claims.userId,
    tenantId: claims.tenantId,
    roles: claims.roles ?? [],
    kycTier: claims.kycTier ?? "KYC0",
    sessionId: claims.sessionId ?? "",
    csrfToken: claims.csrfToken ?? "",
    ...claims.customerId ? { customerId: claims.customerId } : {},
    ...tokens.profile?.fullName ? { name: tokens.profile.fullName } : {},
    ...tokens.profile?.phoneMasked ? { phone: tokens.profile.phoneMasked } : {}
  };
  return user;
}
function establishSession(tokens, clientOptions) {
  const user = buildAuthUser(tokens);
  if (!user) return null;
  const store = useAuthStore.getState();
  store.setSession({ user, accessToken: tokens.accessToken });
  const refreshHandle = scheduleTokenRefresh(tokens.expiresAt, clientOptions);
  authEvents.emit("authStateChanged", { user, isAuthenticated: true });
  return { dispose: () => refreshHandle.stop() };
}
async function initAuthSession(clientOptions = {}) {
  const store = useAuthStore.getState();
  store.setLoading(true);
  const devSession = isAuthDisabled() ? getDevSession() : null;
  if (devSession) {
    const user = buildDevAuthUser(devSession);
    useAuthStore.getState().setSession({ user, accessToken: "" });
    authEvents.emit("authStateChanged", { user, isAuthenticated: true });
    console.warn(
      `[auth] Authentication is DISABLED for local development \u2014 running as "${user.userId}" in tenant "${user.tenantId}". Never expect this in a deployed build.`
    );
    return { dispose: () => {
    } };
  }
  const tokens = await refreshSession(clientOptions);
  const established = tokens ? establishSession(tokens, clientOptions) : null;
  if (established) {
    return established;
  }
  useAuthStore.getState().clearAuth();
  authEvents.emit("authStateChanged", { user: null, isAuthenticated: false });
  return { dispose: () => {
  } };
}
function buildDevAuthUser(dev) {
  return {
    userId: dev.userId,
    tenantId: dev.tenantId,
    roles: dev.roles ?? ["SAVER"],
    kycTier: dev.kycTier ?? "KYC3",
    sessionId: dev.sessionId ?? "local-dev-session",
    csrfToken: "",
    // `QB_DEV_CUSTOMER_ID` already exists precisely because the two ids differ; it
    // now lands on the same field a real token populates, so a screen reading
    // `customerId` behaves identically in both local modes.
    ...dev.customerId ? { customerId: dev.customerId } : {},
    ...dev.name ? { name: dev.name } : {},
    ...dev.phone ? { phone: dev.phone } : {}
  };
}
async function endAuthSession(clientOptions = {}) {
  const accessToken = useAuthStore.getState().accessToken;
  await logoutSession(accessToken ? { ...clientOptions, accessToken } : clientOptions);
  useAuthStore.getState().clearAuth();
  authEvents.emit("authStateChanged", { user: null, isAuthenticated: false });
}

export { buildAuthUser, createRefreshCoordinator, decodeAuthToken, endAuthSession, establishSession, getBrowserDeviceInfo, initAuthSession, isTokenExpired, logoutSession, newAuthRequestId, passwordLogin, refreshSession, registerSaver, requestOtp, resendOtp, scheduleTokenRefresh, sharedRefreshCoordinator, verifyOtp };
//# sourceMappingURL=internal.js.map
//# sourceMappingURL=internal.js.map